import { Color } from './ThemeConstants';
import { hexToRgb, hslToRgbString } from '../../utils/colorUtils';

export function getColorName(color: Color) {
  return Object.keys(Color).filter((key) => isNaN(Number(key)))[color];
}

// function generateColorShades(hexColor: string): Array<string> {
//   const hexRegex = /^#?[0-9a-fA-F]{3}([0-9a-fA-F]{3})?$/;

//   if (!hexRegex.test(hexColor)) {
//     return [];
//   }

//   const steps = [40, 30, 20, 10, 0, -10, -20, -30, -40];

//   return steps.map((step) => {
//     const color = step === 0 ? hexToRgb(hexColor) : adjustColor(hexColor, step);
//     return `${color.r} ${color.g} ${color.b}`;
//   });
// }

export function getSmartGrayCSS(primaryHex: string, isDarkMode: boolean): Record<string, string> {
  const { h } = hexToHsl(primaryHex);
  const vals: Record<string, string> = {};
  const steps = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 950];

  // LIGHT MODE (Unchanged since you liked it)
  const lightMap = [99.5, 92, 85, 75, 60, 50, 40, 30, 20, 12, 4];

  // DARK MODE TUNING
  // 950 (BG): 6% (Lifted from 3% to reduce starkness)
  // 900 (Card): 10% (Closer to BG for a "soft" lift)
  // 800 (Tag): 18% (Keeps the "pop" you liked)
  // 50 (Text): 92% (Dulled from 98% to stop the glow)
  const darkMap = [92, 82, 70, 55, 40, 30, 22, 18, 12, 10, 6];
  // Note: Since the hook flips these, index 10 is your BG, index 0 is your Text.

  const currentMap = isDarkMode ? darkMap : lightMap;

  steps.forEach((step, index) => {
    const saturation = isDarkMode ? 7 : 6; // Slightly lower saturation for "dull" feel
    vals[`--gray-${step}`] = hslToRgbString(h, saturation, currentMap[index]);
  });

  return vals;
}

function generateColorShades(hexColor: string, isDarkMode: boolean): Array<string> {
  const { r, g, b } = hexToRgb(hexColor);
  const { h, s } = hexToHsl(hexColor);

  // baseIndex 3 = Step 300 (Usually for dark mode highlights/buttons)
  // baseIndex 5 = Step 500 (Usually the standard brand color)
  const baseIndex = isDarkMode ? 3 : 5;

  const minL = isDarkMode ? 7 : 10; // Floor (950)
  const maxL = isDarkMode ? 88 : 92; // Ceiling (50)

  return Array.from({ length: 11 }).map((_, i) => {
    // 1. Return exact user color at the anchor
    if (i === baseIndex) return `${r} ${g} ${b}`;

    /**
     * 2. THE POWER CURVE
     * We use 1.4 to ensure the dark end (indices 8, 9, 10)
     * has enough lightness delta to be distinct.
     */
    const ratio = i / 10;
    const curve = Math.pow(ratio, 1.4);
    let newL = maxL - curve * (maxL - minL);

    // 3. SATURATION SCALING
    let newS = s;
    if (newL > 80) {
      newS = Math.max(s, 22); // Saturation floor
    } else if (newL < 40) {
      // Deeper saturation for darker colors (prevents mud)
      const depthFactor = (40 - newL) / 40;
      newS = Math.min(100, s + depthFactor * 20);
    }

    // 4. HUE ROTATION
    const isLightening = i < baseIndex;
    const newH = getHueRotation(h, isLightening);

    return hslToRgbString(newH, newS, newL);
  });
}

export function getShadedColorCSS(hexColor: string, key: string, isDarkMode: boolean): Record<string, string> {
  const shades = generateColorShades(hexColor, isDarkMode);
  const vals: Record<string, string> = {};

  // This array must match the order of tailwindSteps in your generate function
  const tailwindNames = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 950];

  shades.forEach((shade, index) => {
    const name = tailwindNames[index];
    vals[`--${key}-${name}`] = shade;
  });

  return vals;
}

/**
 * Builds the full set of `--user-*` / palette CSS variables for a given brand color set and
 * light/dark mode. `useSiteColors` applies these to `document.body`; callers that need a
 * locally-scoped theme (e.g. an admin preview that flips dark/light independently of the
 * global setting) can apply the same map to any element's `style`, since CSS custom
 * properties inherit down to descendants.
 */
export function buildSiteColorVariables(
  primary: string,
  secondary: string,
  tertiary: string,
  isDarkMode: boolean
): Record<string, string> {
  const pShades = getShadedColorCSS(primary, 'primary', isDarkMode);
  const sShades = getShadedColorCSS(secondary, 'secondary', isDarkMode);
  const tShades = getShadedColorCSS(tertiary, 'tertiary', isDarkMode);
  const gShades = getSmartGrayCSS(primary, isDarkMode);

  const userMappings: Record<string, string> = {};

  // Standard steps for Brand colors
  const brandSteps = [50, 200, 400, 600, 800, 900, 950];

  // DEFINITIVE GRAY MAPPING
  // User-Step:   1 (BG)   2 (Tag)  3 (Bord)  4 (Muted) 5 (Txt3) 6 (Txt2) 7 (Txt1)
  const grayLight = [50, 100, 200, 400, 600, 800, 950];
  const grayDark = [950, 800, 700, 500, 300, 100, 50];

  ['primary', 'secondary', 'tertiary', 'gray'].forEach((type) => {
    const activeSteps = type === 'gray' ? (isDarkMode ? grayDark : grayLight) : brandSteps;

    activeSteps.forEach((shadeValue, index) => {
      const userStep = index + 1;

      let targetShade: number;
      if (type === 'gray') {
        targetShade = activeSteps[index];
      } else {
        targetShade = isDarkMode ? brandSteps[brandSteps.length - 1 - index] : brandSteps[index];
      }

      const paletteKey = type === 'gray' ? `--gray-${targetShade}` : `--${type}-${targetShade}`;
      userMappings[`--user-${type}-${userStep}`] = `var(${paletteKey})`;
    });
  });

  return { ...pShades, ...sShades, ...tShades, ...gShades, ...userMappings };
}

// Perceived Brightness Formula from your provided image
export function getPerceivedBrightness(r: number, g: number, b: number): number {
  return Math.sqrt(0.299 * r ** 2 + 0.587 * g ** 2 + 0.114 * b ** 2) / 255;
}

// Convert HEX to HSL for better manipulation
function hexToHsl(hex: string) {
  let { r, g, b } = hexToRgb(hex); // Assume your existing hexToRgb helper
  r /= 255;
  g /= 255;
  b /= 255;
  const max = Math.max(r, g, b),
    min = Math.min(r, g, b);
  let h = 0,
    s,
    l = (max + min) / 2;

  if (max === min) {
    h = s = 0;
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
    }
    h /= 6;
  }
  return { h: h * 360, s: s * 100, l: l * 100 };
}

// Helper to find nearest Hue Pivot (0, 60, 120, 180, 240, 300)
function getHueRotation(h: number, isLightening: boolean): number {
  const brightPivots = [60, 180, 300];
  const darkPivots = [0, 120, 240, 360];
  const targets = isLightening ? brightPivots : darkPivots;

  // Find closest pivot
  const closest = targets.reduce((prev, curr) => (Math.abs(curr - h) < Math.abs(prev - h) ? curr : prev));

  const dist = closest - h;
  // Rotate subtly (max 10 degrees over the whole scale)
  return h + dist * 0.1;
}
